// Submit / build-payload logic for CallOffWizard.tsx, split out to keep
// CallOffWizard.tsx under the 500-line cap. No behaviour change from the
// original inline handleCreate — moved verbatim.
import { useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, WizardDelivery, WizardDeliveryPart } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { toast } from 'sonner';
import type { Supplier } from './callOffWizardHelpers';
import { summariseDestinations } from './destinationSummary';
import { warnAllocationShortfalls } from './allocationShortfallToast';

interface UseCallOffWizardSubmitArgs {
  selectedSupplier: Supplier | null;
  pickupDate: string;
  openPoDays: number;
  notes: string;
  /** CR-7732: the chosen haulier, or null for none. */
  haulierId: number | null;
  deliveries: WizardDelivery[];
  fillPercent: number;
  isEditMode: boolean;
  editCallOff?: CallOff | null;
  resetWizard: () => void;
  onCreated: () => void;
  onClose: () => void;
}

/**
 * Owns the create/update submission for CallOffWizard: flattening the
 * wizard's deliveries into call-off lines, deriving destination_type /
 * destination_name, and calling the create or update API. See
 * useCallOffWizardState for the rest of the wizard's state.
 */
export function useCallOffWizardSubmit({
  selectedSupplier, pickupDate, openPoDays, notes, haulierId, deliveries, fillPercent,
  isEditMode, editCallOff, resetWizard, onCreated, onClose,
}: UseCallOffWizardSubmitArgs) {
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!selectedSupplier || !pickupDate || deliveries.length === 0) return;

    setSaving(true);
    try {
      // Flatten deliveries into lines
      const lines = deliveries.flatMap(delivery =>
        delivery.parts.map((part: WizardDeliveryPart) => ({
          part_id: part.partId ?? undefined,
          part_number: part.partNumber,
          description: part.description,
          requested_qty: part.qty,
          pallet_qty: part.palletQty,
          load_qty: part.loadQty,
          delivery_type: delivery.type,
          delivery_address: delivery.type === 'direct'
            ? `${delivery.address}, ${delivery.postcode}`
            : '',
          sales_order: delivery.salesOrderId || '',
          delivery_destination_id: delivery.destinationId ?? undefined,
          // CR-8613: the Priority tick, for this call-off only. Always sent for
          // a warehouse line so an untick reaches the backend; direct lines
          // never carry one.
          ...(delivery.type === 'warehouse' ? { is_priority: Boolean(part.isPriority) } : {}),
          // A direct drop has no warehouse to name it, so the name travels with
          // the line. Without it, reopening the call-off could only label the
          // drop with its street address — which then became the call-off's
          // destination_name and overflowed the column (CR-7144).
          delivery_destination_name:
            delivery.type === 'direct' && !delivery.destinationNameIsFallback
              ? delivery.destinationName
              : '',
        }))
      );

      // Determine destination_type
      const types = new Set(deliveries.map(d => d.type));
      let destinationType: 'warehouse' | 'direct' | 'mixed' = 'warehouse';
      if (types.has('warehouse') && types.has('direct')) {
        destinationType = 'mixed';
      } else if (types.has('direct')) {
        destinationType = 'direct';
      }

      // Check fill % > 100% and confirm
      if (fillPercent > 100) {
        const confirmed = window.confirm(
          `Container fill is ${fillPercent}% (over 100%). Are you sure you want to proceed?`
        );
        if (!confirmed) {
          setSaving(false);
          return;
        }
      }

      // Every destination, not just the first: a load to two warehouses used
      // to show only one of them.
      const destinationName = summariseDestinations(deliveries, destinationType);

      if (isEditMode && editCallOff) {
        // Edit mode: PATCH existing call-off
        const updateRes = await callOffAPI.updateCallOff(editCallOff.id, {
          pickup_date: pickupDate,
          open_po_days: openPoDays,
          destination_type: destinationType,
          destination_name: destinationName,
          notes,
          haulier_id: haulierId,
          lines,
        });
        toast.success('Call-off updated successfully');
        // CR-7389: editing a confirmed call-off allocates delivery lines for
        // any line added here. "Updated successfully" on its own would hide a
        // line the PO cannot cover, which then ships short.
        warnAllocationShortfalls(updateRes.data.allocation_shortfalls);
      } else {
        // Create mode
        await callOffAPI.createCallOff({
          supplier_id: selectedSupplier.id,
          pickup_date: pickupDate,
          open_po_days: openPoDays,
          destination_type: destinationType,
          destination_name: destinationName,
          notes,
          haulier_id: haulierId,
          lines,
        });
        toast.success('Call-off created successfully');
      }

      resetWizard();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const fallback = isEditMode ? 'Failed to update call-off' : 'Failed to create call-off';
      toast.error(getApiErrorMessage(err, fallback));
    } finally {
      setSaving(false);
    }
  };

  return { saving, handleCreate };
}
